import React, { useCallback, memo, type KeyboardEvent, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import EditIconOld from '@atlaskit/icon/glyph/edit';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import CategoryTag from '@atlassian/jira-business-categories/src/ui/category-form/category-tag/index.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';
import { OptionWrapper } from './styled.tsx';
import type { Props } from './types.tsx';

const CustomSelectOption = (optionProps: Props) => {
	const {
		onCategoryIsEditing,
		categoryBeenEdited,
		label,
		value: optionId,
		color,
		isCreateOption = false,
		isFocused,
		selectedOption,
		setCategoryToDeleteInModal,
	} = optionProps;
	const isOptionBeingEdited = categoryBeenEdited === label;
	const isEditing = categoryBeenEdited != null;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const project = useProject();
	const hasAdminPermission = project.permissions.administerProject;

	const handleEditClick = useCallback(
		(e: SyntheticEvent) => {
			e.stopPropagation();
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'button clicked',
				'jwmListViewCategoryOptionButtonEdit',
			);
			onCategoryIsEditing(label, optionId, color);
		},
		[createAnalyticsEvent, onCategoryIsEditing, label, optionId, color],
	);

	const handleDeleteClick = useCallback(
		(e: SyntheticEvent) => {
			e.stopPropagation();
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'button clicked',
				'jwmListViewCategoryOptionButtonDelete',
			);
			setCategoryToDeleteInModal({
				label,
				value: optionId,
				color,
			});
		},
		[color, createAnalyticsEvent, label, optionId, setCategoryToDeleteInModal],
	);

	const handleEditKeyDownOld = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleEditClick(e);
			}
		},
		[handleEditClick],
	);

	const handleEditKeyDown = useCallback(
		(e: KeyboardEvent<HTMLButtonElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleEditClick(e);
			}
		},
		[handleEditClick],
	);

	const handleDeleteKeyDownOld = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleDeleteClick(e);
			}
		},
		[handleDeleteClick],
	);

	const handleDeleteKeyDown = useCallback(
		(e: KeyboardEvent<HTMLButtonElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleDeleteClick(e);
			}
		},
		[handleDeleteClick],
	);

	return (
		<OptionWrapper
			isOptionBeingEdited={isOptionBeingEdited}
			isEditing={isEditing}
			testId={`business-list.ui.list-view.category-cell.edit-category-cell.custom-select-option-${label}`}
		>
			{(!isEditing || !isOptionBeingEdited) && (
				<CategoryWrapper>
					<CategoryTag categoryColor={color} categoryName={label} />
				</CategoryWrapper>
			)}
			{hasAdminPermission &&
				!isCreateOption &&
				!isEditing &&
				isFocused &&
				(isVisualRefreshEnabled() && fg('jira_nav4_beta_drop_1') ? (
					<Box xcss={optionIconsWrapperStyles}>
						<IconButton
							icon={EditIcon}
							appearance="subtle"
							spacing="compact"
							label={formatMessage(messages.rename)}
							onKeyDown={handleEditKeyDown}
							onClick={handleEditClick}
							testId="business-list.ui.list-view.category-cell.edit-category-cell.custom-select-option.edit-button"
						/>
						<IconButton
							icon={DeleteIcon}
							appearance="subtle"
							spacing="compact"
							label={formatMessage(messages.delete)}
							onKeyDown={handleDeleteKeyDown}
							onClick={handleDeleteClick}
							testId="business-list.ui.list-view.category-cell.edit-category-cell.custom-select-option.delete-button"
						/>
					</Box>
				) : (
					<OptionIconsWrapper isHoveringOption={selectedOption === label}>
						<Button
							aria-label={formatMessage(messages.rename)}
							iconBefore={<EditIconOld size="small" label={formatMessage(messages.rename)} />}
							onKeyDown={handleEditKeyDownOld}
							onClick={handleEditClick}
							testId="business-list.ui.list-view.category-cell.edit-category-cell.custom-select-option.edit-button"
						/>
						<Button
							aria-label={formatMessage(messages.delete)}
							iconBefore={<TrashIcon size="small" label={formatMessage(messages.delete)} />}
							onKeyDown={handleDeleteKeyDownOld}
							onClick={handleDeleteClick}
							testId="business-list.ui.list-view.category-cell.edit-category-cell.custom-select-option.delete-button"
						/>
					</OptionIconsWrapper>
				))}
		</OptionWrapper>
	);
};

const optionIconsWrapperStyles = xcss({
	position: 'absolute',
	right: token('space.0', '0px'),
	paddingLeft: 'space.075',
	paddingRight: 'space.075',
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	gap: 'space.050',
});

export default memo<Props>(CustomSelectOption);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const CategoryWrapper = styled.div({
	width: 'calc(100% - 50px)',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const OptionIconsWrapper = styled.div<{ isHoveringOption: boolean }>({
	position: 'absolute',
	right: token('space.0', '0px'),
	padding: `0 ${token('space.075', '6px')}`,
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		background: 'transparent',
	},
});
